import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Search } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useScrollPosition } from '../../hooks/useScrollPosition';
import { companyInfo } from '../../data/mockData';
import {
  DesktopNav,
  MobileMenu,
  LanguageDropdown,
  SearchBar,
  useNavItems,
} from './HeaderParts';

const Header = () => {
  const isScrolled = useScrollPosition(50);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [showLangDropdown, setShowLangDropdown] = useState(false);
  const location = useLocation();
  const { currentLang, changeLang, t, languages, isRTL } = useLanguage();

  const navigation = useNavItems(t);
  const currentLangConfig = languages.find((l) => l.code === currentLang);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const handleLanguageChange = (code) => {
    changeLang(code);
    setShowLangDropdown(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md py-2' : 'bg-white/95 py-4'
      }`}
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <img
              src={companyInfo.logo}
              alt="Boidy Logo"
              className="h-12 w-auto object-contain"
            />
          </Link>

          <DesktopNav navigation={navigation} pathname={location.pathname} />

          <div className={`flex items-center gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <LanguageDropdown
              isOpen={showLangDropdown}
              onToggle={() => setShowLangDropdown(!showLangDropdown)}
              currentLang={currentLang}
              currentLangConfig={currentLangConfig}
              languages={languages}
              changeLang={handleLanguageChange}
              isRTL={isRTL}
            />

            <button
              onClick={() => setIsSearchOpen(!isSearchOpen)}
              className="p-2 text-gray-600 hover:text-[#9B4B8C] transition-colors"
              data-testid="header-search-toggle"
            >
              <Search className="w-5 h-5" />
            </button>

            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 text-gray-600 hover:text-[#9B4B8C] transition-colors"
              data-testid="header-mobile-menu-toggle"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        <SearchBar isOpen={isSearchOpen} isRTL={isRTL} currentLang={currentLang} />
      </div>

      <MobileMenu
        isOpen={isMobileMenuOpen}
        navigation={navigation}
        pathname={location.pathname}
        languages={languages}
        currentLang={currentLang}
        changeLang={changeLang}
      />
    </header>
  );
};

export default Header;
